import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Gavel, Loader2, Pencil, Plus, ReceiptText, Search, Trash2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, errorMessage } from "@/lib/api";
import { money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { SearchPicker } from "@/components/SearchPicker";
import { SuggestInput } from "@/components/SuggestInput";
import { PER_PAGE, Pager } from "@/components/Pager";

const BLANK = {
  title: "",
  forum: "",
  matter_ref: "",
  represented_party: "",
  party_role: "Petitioner",
  client_id: "",
  contact_id: "",
  contact_ids: [],
  opposing_party: "",
  status: "active",
  notes: "",
};

const ROLES = ["Petitioner", "Respondent", "Appellant", "Plaintiff", "Defendant", "Claimant", "Applicant"];

const BLANK_PERSON = {
  full_name: "",
  designation: "",
  email: "",
  whatsapp: "",
  attach: "client",
  firm_id: "",
};

export default function Cases() {
  const { currency, user } = useAuth();
  const isOwner = user?.role !== "clerk";
  const [cases, setCases] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [clients, setClients] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [forumFilter, setForumFilter] = useState("all");
  const [personOpen, setPersonOpen] = useState(false);
  const [person, setPerson] = useState(BLANK_PERSON);
  const [clientOpen, setClientOpen] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", city: "", address: "" });
  const [nesting, setNesting] = useState(false);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    try {
      const [caseRes, contactRes, clientRes, firmRes] = await Promise.all([
        api.get("/cases"),
        api.get("/contacts"),
        api.get("/clients"),
        api.get("/firms"),
      ]);
      setCases(caseRes.data);
      setContacts(contactRes.data);
      setClients(clientRes.data);
      setFirms(firmRes.data);
    } catch (error) {
      toast.error(errorMessage(error, "Could not load matters"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const forums = useMemo(
    () => [...new Set(cases.map((c) => c.forum).filter(Boolean))].sort(),
    [cases],
  );

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return cases.filter((c) => {
      if (forumFilter !== "all" && c.forum !== forumFilter) return false;
      if (!needle) return true;
      return [c.title, c.matter_ref, c.represented_party, c.client_name, c.contact_name, c.firm_name, c.forum]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(needle));
    });
  }, [cases, query, forumFilter]);

  useEffect(() => {
    setPage(1);
  }, [query, forumFilter]);

  useEffect(() => {
    setQuery(searchParams.get("q") || "");
  }, [searchParams]);

  const paged = rows.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const openNew = () => {
    setEditing(null);
    setForm(BLANK);
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      ...BLANK,
      ...item,
      client_id: item.client_id || "",
      contact_id: item.contact_id || "",
      contact_ids: item.contact_ids || (item.contact_id ? [item.contact_id] : []),
    });
    setOpen(true);
  };

  const onClientChange = (value) => {
    const clientId = value === "none" ? "" : value;
    const auto = contacts.filter((c) => c.client_id && c.client_id === clientId).map((c) => c.contact_id);
    setForm((f) => {
      const merged = [...new Set([...f.contact_ids, ...auto])];
      const client = clients.find((c) => c.client_id === clientId);
      return {
        ...f,
        client_id: clientId,
        contact_ids: merged,
        contact_id: f.contact_id || merged[0] || "",
        represented_party: client?.name || f.represented_party,
      };
    });
  };

  const togglePerson = (contactId) =>
    setForm((f) => {
      const on = f.contact_ids.includes(contactId);
      const next = on
        ? f.contact_ids.filter((id) => id !== contactId)
        : [...f.contact_ids, contactId];
      return {
        ...f,
        contact_ids: next,
        contact_id: on && f.contact_id === contactId ? next[0] || "" : f.contact_id || contactId,
      };
    });

  const savePerson = async (event) => {
    event.preventDefault();
    if (!person.full_name.trim()) {
      toast.error("Name of the contact person is required");
      return;
    }
    setNesting(true);
    try {
      const payload = {
        full_name: person.full_name,
        designation: person.designation,
        email: person.email,
        whatsapp: person.whatsapp,
        client_id: person.attach === "client" ? form.client_id || null : null,
        firm_id: person.attach === "firm" ? person.firm_id || null : null,
        is_direct_client: person.attach === "none",
      };
      const { data } = await api.post("/contacts", payload);
      const { data: fresh } = await api.get("/contacts");
      setContacts(fresh);
      setForm((f) => ({
        ...f,
        contact_ids: [...new Set([...f.contact_ids, data.contact_id])],
        contact_id: f.contact_id || data.contact_id,
      }));
      setPerson(BLANK_PERSON);
      setPersonOpen(false);
      toast.success(`${data.full_name} added to this matter`);
    } catch (error) {
      toast.error(errorMessage(error, "Could not add the contact person"));
    } finally {
      setNesting(false);
    }
  };

  const saveClient = async (event) => {
    event.preventDefault();
    if (!newClient.name.trim()) {
      toast.error("Client name is required");
      return;
    }
    setNesting(true);
    try {
      const { data } = await api.post("/clients", newClient);
      const { data: fresh } = await api.get("/clients");
      setClients(fresh);
      setForm((f) => ({ ...f, client_id: data.client_id, represented_party: data.name }));
      setNewClient({ name: "", city: "", address: "" });
      setClientOpen(false);
      toast.success(`${data.name} added`);
    } catch (error) {
      toast.error(errorMessage(error, "Could not add the client"));
    } finally {
      setNesting(false);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) {
      toast.error("Case title is required");
      return;
    }
    if (form.contact_ids.length > 0 && !form.contact_id) {
      toast.error("Mark one contact person as the billing contact");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title,
      forum: form.forum,
      matter_ref: form.matter_ref,
      represented_party: form.represented_party,
      party_role: form.party_role,
      client_id: form.client_id || null,
      contact_id: form.contact_id || null,
      contact_ids: form.contact_ids,
      opposing_party: form.opposing_party,
      status: form.status,
      notes: form.notes,
    };
    try {
      if (editing) await api.put(`/cases/${editing.case_id}`, payload);
      else await api.post("/cases", payload);
      toast.success(editing ? "Matter updated" : "Matter added");
      setOpen(false);
      load();
    } catch (error) {
      toast.error(errorMessage(error, "Could not save matter"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.title}?`)) return;
    try {
      await api.delete(`/cases/${item.case_id}`);
      toast.success("Matter deleted");
      load();
    } catch (error) {
      toast.error(errorMessage(error, "Could not delete matter"));
    }
  };

  const field = (key) => ({
    value: form[key] ?? "",
    onChange: (e) => setForm((f) => ({ ...f, [key]: e.target.value })),
  });

  const orgOf = (c) => c.firm_name || c.client_name || "Direct";
  const sortedContacts = useMemo(() => {
    const score = (c) => {
      if (form.client_id && c.client_id === form.client_id) return 0;
      if (c.firm_id) return 1;
      return 2;
    };
    return [...contacts].sort(
      (a, b) => score(a) - score(b) || (a.full_name || "").localeCompare(b.full_name || ""),
    );
  }, [contacts, form.client_id]);

  const clientItems = useMemo(
    () => clients.map((c) => ({ id: c.client_id, label: c.name, hint: c.city || "" })),
    [clients],
  );
  const firmItems = useMemo(
    () => firms.map((f) => ({ id: f.firm_id, label: f.name, hint: f.city || "" })),
    [firms],
  );
  const contactItems = useMemo(
    () =>
      sortedContacts.map((c) => ({
        id: c.contact_id,
        label: c.full_name,
        hint: [c.firm_name || c.client_name || "Direct", c.email].filter(Boolean).join(" · "),
      })),
    [sortedContacts],
  );

  return (
    <div className="space-y-8" data-testid="cases-page">
      <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="label-caps">Register of briefs</p>
          <h1 className="mt-3 font-serif text-3xl tracking-tight text-slate-900 lg:text-4xl">
            Matters
          </h1>
          <p className="mt-3 max-w-xl text-sm text-slate-600">
            Each matter carries its client and every contact person on the brief. One of them is
            marked the billing contact; the fee note goes to all of them.
          </p>
        </div>
        <button
          type="button"
          onClick={openNew}
          data-testid="add-case-button"
          className="inline-flex items-center gap-2 bg-slate-900 px-5 py-2.5 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" strokeWidth={2} />
          Add matter
        </button>
      </header>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by case title, matter reference, client, solicitor…"
            className="rounded-sm pl-9"
            data-testid="case-search-input"
          />
        </div>
        <div className="w-full sm:w-64">
          <SearchPicker
            items={[
              { id: "all", label: "All forums / courts" },
              ...forums.map((f) => ({ id: f, label: f })),
            ]}
            value={forumFilter}
            onSelect={(v) => setForumFilter(v || "all")}
            placeholder="All forums / courts"
            testId="case-forum-filter"
            emptyLabel="No forum matches that text."
          />
        </div>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : rows.length === 0 ? (
        <div className="surface p-10 text-center" data-testid="cases-empty">
          <Gavel className="mx-auto h-6 w-6 text-slate-300" strokeWidth={1.4} />
          <p className="mt-4 text-sm text-slate-600">No matters found.</p>
        </div>
      ) : (
        <div className="space-y-6">
        <div className="grid gap-4 lg:grid-cols-2" data-testid="cases-list">
          {paged.map((item) => (
            <article
              key={item.case_id}
              className="surface flex flex-col p-6"
              data-testid={`case-card-${item.case_id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="num text-[11px] uppercase tracking-[0.14em] text-slate-400">
                    {item.matter_ref || "No reference"}
                  </p>
                  <h2 className="mt-2 font-serif text-lg leading-snug text-slate-900">
                    {item.title}
                  </h2>
                  <p className="mt-1 text-[12px] text-slate-500">{item.forum || "Forum not set"}</p>
                </div>
                <div className="flex gap-1">
                  <Link
                    to={`/invoices/new?case=${item.case_id}`}
                    data-testid={`bill-case-${item.case_id}`}
                    title="Raise fee note"
                    className="flex h-8 w-8 items-center justify-center border border-slate-200 text-slate-500 transition-colors duration-150 hover:border-slate-900 hover:text-slate-900"
                  >
                    <ReceiptText className="h-3.5 w-3.5" strokeWidth={1.6} />
                  </Link>
                  <button
                    type="button"
                    onClick={() => openEdit(item)}
                    data-testid={`edit-case-${item.case_id}`}
                    className="flex h-8 w-8 items-center justify-center border border-slate-200 text-slate-500 transition-colors duration-150 hover:border-slate-400 hover:text-slate-900"
                  >
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.6} />
                  </button>
                  {isOwner && (
                  <button
                    type="button"
                    onClick={() => remove(item)}
                    data-testid={`delete-case-${item.case_id}`}
                    className="flex h-8 w-8 items-center justify-center border border-slate-200 text-slate-500 transition-colors duration-150 hover:border-red-300 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} />
                  </button>
                  )}
                </div>
              </div>

              <dl className="mt-5 grid grid-cols-2 gap-4 border-t border-slate-100 pt-4 text-[12px]">
                <div>
                  <dt className="label-caps">Client</dt>
                  <dd className="mt-1 text-slate-800">
                    {item.client_name || item.represented_party || "—"}
                    {item.party_role ? (
                      <span className="text-slate-400"> · {item.party_role}</span>
                    ) : null}
                  </dd>
                </div>
                <div>
                  <dt className="label-caps">Billing contact</dt>
                  <dd className="mt-1 text-slate-800">
                    {item.contact_name || "Not assigned"}
                    {item.firm_name ? (
                      <span className="block text-slate-400">{item.firm_name}</span>
                    ) : null}
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="label-caps">Contact persons on the brief</dt>
                  <dd
                    className="mt-1.5 flex flex-wrap gap-1.5"
                    data-testid={`case-contacts-${item.case_id}`}
                  >
                    {(item.contacts || []).length === 0 ? (
                      <span className="text-slate-500">None yet</span>
                    ) : (
                      (item.contacts || []).map((p) => (
                        <span
                          key={p.contact_id}
                          className={`border px-2 py-0.5 text-[11px] ${
                            p.is_billing
                              ? "border-slate-900 bg-slate-900 text-white"
                              : "border-slate-200 text-slate-600"
                          }`}
                        >
                          {p.full_name}
                          {p.is_billing ? " · bills" : ""}
                        </span>
                      ))
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="label-caps">Billed to date</dt>
                  <dd className="num mt-1 text-slate-900">{money(item.billed_total, currency)}</dd>
                </div>
                <div>
                  <dt className="label-caps">Status</dt>
                  <dd className="mt-1 capitalize text-slate-800">{item.status}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
        <Pager page={page} total={rows.length} onPage={setPage} testId="cases-pager" />
        </div>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle className="font-serif text-xl">
              {editing ? "Edit matter" : "Add matter"}
            </SheetTitle>
            <SheetDescription className="text-[12px]">
              Link the client, tick every contact person on the brief, and mark the one to be
              billed.
            </SheetDescription>
          </SheetHeader>
          <form className="mt-7 space-y-5" onSubmit={save} data-testid="case-form">
            <div className="space-y-2">
              <Label className="label-caps">Case title *</Label>
              <Input
                {...field("title")}
                placeholder="ABC Pvt. Ltd. v. Union of India"
                data-testid="case-title-input"
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="label-caps">Forum / court</Label>
                <SuggestInput
                  value={form.forum || ""}
                  onChange={(v) => setForm((f) => ({ ...f, forum: v }))}
                  suggestions={forums}
                  placeholder="Start typing — e.g. Bombay High Court"
                  testId="case-forum-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Matter reference #</Label>
                <Input {...field("matter_ref")} data-testid="case-ref-input" className="rounded-sm" />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="label-caps">Client</Label>
                <button
                  type="button"
                  onClick={() => setClientOpen(true)}
                  data-testid="new-client-inline-button"
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 transition-colors duration-150 hover:text-slate-900"
                >
                  <Plus className="h-3 w-3" strokeWidth={2.2} />
                  New client
                </button>
              </div>
              <SearchPicker
                items={clientItems}
                value={form.client_id}
                onSelect={onClientChange}
                placeholder="Start typing the client's name…"
                testId="case-client"
                allowNone
                noneLabel="Not linked to a client record"
                emptyLabel="No client matches that text — add one above."
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="label-caps">Represented party (as on record)</Label>
                <Input
                  {...field("represented_party")}
                  data-testid="case-party-input"
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Party role</Label>
                <Select
                  value={form.party_role || "Petitioner"}
                  onValueChange={(v) => setForm((f) => ({ ...f, party_role: v }))}
                >
                  <SelectTrigger className="rounded-sm" data-testid="case-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="label-caps">Contact persons on this matter</Label>
                <button
                  type="button"
                  onClick={() => {
                    setPerson({ ...BLANK_PERSON, attach: form.client_id ? "client" : "firm" });
                    setPersonOpen(true);
                  }}
                  data-testid="new-contact-inline-button"
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 transition-colors duration-150 hover:text-slate-900"
                >
                  <UserPlus className="h-3 w-3" strokeWidth={2.2} />
                  New contact person
                </button>
              </div>
              <SearchPicker
                items={contactItems}
                values={form.contact_ids}
                multi
                onToggle={togglePerson}
                placeholder="Start typing a name to add them to this brief…"
                testId="case-contact"
                emptyLabel="Nobody matches that text — add a new contact person above."
              />
              <div className="flex flex-wrap gap-2" data-testid="case-contact-selected">
                {form.contact_ids.length === 0 ? (
                  <p className="text-[12px] text-slate-500">
                    Nobody picked yet — search above to add the people on this brief.
                  </p>
                ) : (
                  form.contact_ids.map((id) => {
                    const person = contacts.find((c) => c.contact_id === id);
                    if (!person) return null;
                    const bills = form.contact_id === id;
                    return (
                      <span
                        key={id}
                        className={`inline-flex items-center gap-2 border px-2.5 py-1 text-[12px] ${
                          bills ? "border-slate-900 bg-slate-50" : "border-slate-300"
                        }`}
                      >
                        <span className="text-slate-800">{person.full_name}</span>
                        <button
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, contact_id: id }))}
                          data-testid={`case-billing-pick-${id}`}
                          className={`border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] transition-colors duration-150 ${
                            bills
                              ? "border-slate-900 bg-slate-900 text-white"
                              : "border-slate-300 text-slate-600 hover:border-slate-900"
                          }`}
                        >
                          {bills ? "Bills" : "Bill"}
                        </button>
                        <button
                          type="button"
                          onClick={() => togglePerson(id)}
                          data-testid={`case-contact-remove-${id}`}
                          className="text-slate-400 transition-colors duration-150 hover:text-red-600"
                        >
                          <X className="h-3 w-3" strokeWidth={2.4} />
                        </button>
                      </span>
                    );
                  })
                )}
              </div>
              <p className="text-[11px] text-slate-500">
                {form.contact_ids.length} on the brief · the fee note is addressed to the one marked
                &ldquo;Bills&rdquo; and emailed to all of them.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="label-caps">Opposing party</Label>
                <Input
                  {...field("opposing_party")}
                  data-testid="case-opposing-input"
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Status</Label>
                <Select
                  value={form.status || "active"}
                  onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
                >
                  <SelectTrigger className="rounded-sm" data-testid="case-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disposed">Disposed</SelectItem>
                    <SelectItem value="on_hold">On hold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Notes</Label>
              <Textarea {...field("notes")} rows={2} data-testid="case-notes-input" className="rounded-sm" />
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="save-case-button"
              className="inline-flex w-full items-center justify-center gap-2 bg-slate-900 px-5 py-3 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {editing ? "Save changes" : "Add matter"}
            </button>
          </form>
        </SheetContent>
      </Sheet>

      <Dialog open={personOpen} onOpenChange={setPersonOpen}>
        <DialogContent className="sm:max-w-md" data-testid="new-contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">New contact person</DialogTitle>
            <DialogDescription className="text-[12px]">
              Added to this matter straight away, with their own email and mobile.
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={savePerson}>
            <div className="space-y-2">
              <Label className="label-caps">Full name *</Label>
              <Input
                value={person.full_name}
                onChange={(e) => setPerson((p) => ({ ...p, full_name: e.target.value }))}
                data-testid="person-name-input"
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="label-caps">Designation</Label>
                <Input
                  value={person.designation}
                  onChange={(e) => setPerson((p) => ({ ...p, designation: e.target.value }))}
                  data-testid="person-designation-input"
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Mobile / WhatsApp</Label>
                <Input
                  value={person.whatsapp}
                  onChange={(e) => setPerson((p) => ({ ...p, whatsapp: e.target.value }))}
                  placeholder="+91 98xxx xxxxx"
                  data-testid="person-whatsapp-input"
                  className="num rounded-sm"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Email</Label>
              <Input
                value={person.email}
                onChange={(e) => setPerson((p) => ({ ...p, email: e.target.value }))}
                type="email"
                data-testid="person-email-input"
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Attached to</Label>
              <Select
                value={person.attach}
                onValueChange={(v) => setPerson((p) => ({ ...p, attach: v }))}
              >
                <SelectTrigger className="rounded-sm" data-testid="person-attach-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client" disabled={!form.client_id}>
                    The client on this matter
                  </SelectItem>
                  <SelectItem value="firm">An instructing law firm</SelectItem>
                  <SelectItem value="none">Neither — independent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {person.attach === "firm" && (
              <div className="space-y-2">
                <Label className="label-caps">Law firm</Label>
                <SearchPicker
                  items={firmItems}
                  value={person.firm_id}
                  onSelect={(v) => setPerson((p) => ({ ...p, firm_id: v }))}
                  placeholder="Start typing the firm's name…"
                  testId="person-firm"
                  allowNone
                  noneLabel="Not set"
                  emptyLabel="No firm matches that text."
                />
              </div>
            )}
            <button
              type="submit"
              disabled={nesting}
              data-testid="save-person-button"
              className="inline-flex w-full items-center justify-center gap-2 bg-slate-900 px-5 py-3 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
            >
              {nesting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Add &amp; tick on this matter
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={clientOpen} onOpenChange={setClientOpen}>
        <DialogContent className="sm:max-w-md" data-testid="new-client-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">New client</DialogTitle>
            <DialogDescription className="text-[12px]">
              You can fill in the full address, GST number and contact persons later on the Clients
              page.
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={saveClient}>
            <div className="space-y-2">
              <Label className="label-caps">Client name *</Label>
              <Input
                value={newClient.name}
                onChange={(e) => setNewClient((c) => ({ ...c, name: e.target.value }))}
                data-testid="inline-client-name-input"
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">City</Label>
              <Input
                value={newClient.city}
                onChange={(e) => setNewClient((c) => ({ ...c, city: e.target.value }))}
                data-testid="inline-client-city-input"
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Address</Label>
              <Textarea
                value={newClient.address}
                onChange={(e) => setNewClient((c) => ({ ...c, address: e.target.value }))}
                rows={3}
                data-testid="inline-client-address-input"
                className="rounded-sm"
              />
            </div>
            <button
              type="submit"
              disabled={nesting}
              data-testid="save-inline-client-button"
              className="inline-flex w-full items-center justify-center gap-2 bg-slate-900 px-5 py-3 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
            >
              {nesting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Add client
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
